import { NEEDS_OWNER, gapCount, type Doc } from './documents'

/**
 * The words of one legal document, rendered — ONE renderer for the two places the
 * document is shown: the in-app sheet (LegalSheet) and the public page at /privacy
 * and /terms (LegalPage). One source (documents.ts) and one renderer means the text
 * App Store Connect links to and the text the app shows cannot drift apart; the two
 * differ only in the frame around this.
 *
 * THE UNFINISHED PARTS ARE VISIBLE, deliberately. Every `⟦to be filled in⟧` is drawn
 * as a marked placeholder and counted in a banner at the top. A draft that hides its
 * gaps reads as finished — which is exactly how an unfinished policy gets published,
 * because the person approving it cannot see what is missing. Here the count is the
 * first thing on the screen.
 */
export function LegalBody({ doc }: { doc: Doc }) {
  const gaps = gapCount(doc)
  return (
    <>
      <p className="legal-standfirst">{doc.standfirst}</p>
      {gaps > 0 && (
        <p className="legal-draft" role="status">
          Draft — {gaps} {gaps === 1 ? 'passage' : 'passages'} still to be written.
          Not to be published in this state.
        </p>
      )}
      {doc.sections.map((s) => (
        <section key={s.heading} className="legal-sec">
          <h3>{s.heading}</h3>
          {s.body.map((para, i) => (
            <p key={i}>{marked(para)}</p>
          ))}
        </section>
      ))}
      <p className="legal-updated">Last updated {doc.updated}</p>
    </>
  )
}

/**
 * Draw the placeholder as a placeholder.
 *
 * Split rather than `dangerouslySetInnerHTML`: this text is edited by hand and will
 * one day be edited by someone pasting from a word processor, so it must never be
 * able to carry markup into the page.
 */
function marked(text: string) {
  const parts = text.split(NEEDS_OWNER)
  if (parts.length === 1) return text
  return parts.flatMap((part, i) =>
    i === 0 ? [part] : [<mark key={i} className="legal-gap">{NEEDS_OWNER}</mark>, part],
  )
}
